import React from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface EmptyStateProps {
    type: 'camps' | 'sessions';
    hasSearchCriteria?: boolean;
    onClearSearch?: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ type, hasSearchCriteria = false, onClearSearch }) => {
    const isCamps = type === 'camps';
    
    return (
        <View style={styles.container}>
            <View style={styles.iconContainer}>
                <MaterialCommunityIcons 
                    name={isCamps ? "map-marker-off" : "calendar-remove"} 
                    size={80} 
                    color="#E0E0E0" 
                />
            </View>
            
            <Text style={styles.title}>
                {hasSearchCriteria 
                    ? `No ${isCamps ? 'camps' : 'sessions'} found`
                    : `No ${isCamps ? 'camps' : 'sessions'} available`
                }
            </Text>
            
            <Text style={styles.description}>
                {hasSearchCriteria ? (
                    <>
                        We couldn't find any {isCamps ? 'camps' : 'sessions'} matching your criteria.{'\n'}
                        Try adjusting your filters or search location.
                    </>
                ) : (
                    <>
                        There are no {isCamps ? 'camps' : 'sessions'} in this area yet.{'\n'}
                        Check back later or try a different location.
                    </>
                )}
            </Text>

            {hasSearchCriteria && onClearSearch && (
                <Pressable style={styles.button} onPress={onClearSearch}>
                    <MaterialCommunityIcons name="tune-variant" size={20} color="white" />
                    <Text style={styles.buttonText}>Clear Filters</Text>
                </Pressable>
            )}

            <View style={styles.tipsContainer}>
                <Text style={styles.tipsTitle}>Tips:</Text>
                <View style={styles.tipRow}>
                    <MaterialCommunityIcons name="circle-small" size={20} color="#666" />
                    <Text style={styles.tipText}>Try expanding your search radius</Text>
                </View>
                <View style={styles.tipRow}>
                    <MaterialCommunityIcons name="circle-small" size={20} color="#666" />
                    <Text style={styles.tipText}>Remove date restrictions</Text>
                </View>
                <View style={styles.tipRow}>
                    <MaterialCommunityIcons name="circle-small" size={20} color="#666" />
                    <Text style={styles.tipText}>Search for a different location</Text>
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 80,
        paddingHorizontal: 40,
        minHeight: 500,
    },
    iconContainer: {
        width: 140,
        height: 140,
        borderRadius: 70,
        backgroundColor: '#F5F5F5',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 24,
    },
    title: {
        fontSize: 28,
        fontWeight: '700',
        color: '#333',
        marginBottom: 12,
        textAlign: 'center',
    },
    description: {
        fontSize: 16,
        color: '#666',
        textAlign: 'center',
        lineHeight: 24,
        marginBottom: 32,
        maxWidth: 500,
    },
    button: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#3C8248',
        paddingHorizontal: 24,
        paddingVertical: 14,
        borderRadius: 12,
        marginBottom: 40,
        shadowColor: '#2C67D6',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 4,
    },
    buttonText: {
        fontSize: 16,
        fontWeight: '600',
        color: 'white',
        marginLeft: 8,
    },
    tipsContainer: {
        backgroundColor: '#F8F9FA',
        padding: 24,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#E0E0E0',
        width: '100%',
        maxWidth: 400,
    },
    tipsTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: '#333',
        marginBottom: 12,
    },
    tipRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 8,
    },
    tipText: {
        fontSize: 14,
        color: '#666',
        marginLeft: -4,
    },
});

export default EmptyState;

